import { logger } from "~/utils/logging";

import type { I18nDict, I18nDictEntry, I18nEntryOptions } from "./context";

/**
 * Retrieves a dictionary value by dot notation.
 */
export function getDictValue(
  dict: I18nDict,
  key: string,
  options?: I18nEntryOptions,
) {
  const value = getPluralizedKey(key, options?.count)
    .split(".")
    .reduce((prev, curr) => prev?.[curr] as I18nDictEntry, dict);
  return typeof value === "string" ? value : undefined;
}

/**
 * Generates a pluralized version of the key.
 */
export function getPluralizedKey(key: string, count?: number) {
  if (
    !Number.isInteger(count) ||
    !key.match(/^[a-zA-z0-9_-]+(\.[a-zA-z0-9_-]+)*$/g)
  )
    return key;
  // TODO: [CLASS-877] Extend localization of plural keys
  switch (count) {
    case 1:
      return `${key}_one`;
    default:
      return `${key}_other`;
  }
}

/**
 * Replaces template {{...}} values with provided data.
 */
export function interpolateDictEntry(
  entry: string,
  options?: I18nEntryOptions,
) {
  return [...entry.matchAll(/{{([\w|\s]+)}}/g)].reduce((acc, curr) => {
    const [match, key] = curr;
    return acc.replace(match, options?.[key.trim()] ?? "");
  }, entry);
}

/**
 * Translates a string using the provided dictionary.
 */
export function translate(
  dict: I18nDict,
  key: string,
  options?: I18nEntryOptions,
  fallback?: string | Record<string, string>,
) {
  const fromDict = getDictValue(dict, key, options);

  const fromFallback: string =
    typeof fallback === "string"
      ? fallback
      : (fallback as Record<string, string>)?.[
          getPluralizedKey(key, options?.count)
        ];

  const entry = fromDict ?? fromFallback;

  if (!entry) {
    logger.warn(`Missing label: ${key}`);
  }

  return interpolateDictEntry(entry ?? key, options);
}
